import { tagName } from "@ember-decorators/component"
import Component from "@ember/component"
import { action, computed, get, set, setProperties } from "@ember/object"
import { inject as service } from "@ember/service"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Session from "cc-frontend/services/session"
import type { Moment } from "cc-frontend/utils/extended-moment"
import moment from "cc-frontend/utils/extended-moment"
import { task, timeout } from "ember-concurrency"
import { scheduler, Token } from "ember-raf-scheduler"
import _ from "lodash"
import { first, forEach, includes, isNil, last, map, max } from "lodash-es"
import { focusCourse } from "react-app/components/planner/card-stacks/unified-lesson-editable/focused-lesson"
import { tracked } from "tracked-built-ins"
import { decorateDates } from "../../../lib/date-formatter"
import draggableLessons from "../../../mixins/draggable-lessons"
import styles from "./month.module.css"

@tagName("")
export default class Month extends Component.extend(draggableLessons) {
  readonly styles = styles

  // Args
  planbook!: Planbook
  date!: string
  rotationCalendar!: RotationCalendar
  setNewDate!: Function
  courses!: Array<Course>
  course!: Course
  isFilteringClasses!: boolean
  lessonsCollapsed!: boolean
  collapseLessons!: boolean
  hideNav!: boolean
  // daysAsRows!: boolean
  classesLinedUp!: boolean
  hiddenCourseIds: Array<string> = []

  // State
  hasStoppedScrolling = null
  _groups = {}
  sidebarIsVisible = true
  focusedCourseId = null
  dateStringForSidebar = null
  daysAsRows = false
  dateRangeString
  dateStrings!: { dateString: string }[]
  dateObjects
  dateToShowAtTop
  currentYearMonth
  _oldDateString: any = null

  @tracked isPinned = false

  // Services
  @service declare modalManager: ModalManager
  @service declare session: Session

  @service("ember-store") store
  @service appState
  @service dragging

  didReceiveAttrs() {
    // Set the date
    if (this._oldDateString === this.date) return
    this._oldDateString = this.date

    let validSchoolDays = this.rotationCalendar.attributes.schoolDays
    let date = this.date

    // If we're focusing on it in the sidebar, don't move it
    if (this.dateStringForSidebar === date) return

    let startDate = moment(date).startOf("month").startOf("week")
    let endDate = moment(date).endOf("month").endOf("week")

    let range = moment.range(startDate, endDate)
    let acc: Moment[] = []
    Array.from(range.by("days")).map((moment) => {
      if (includes(validSchoolDays, moment.weekday())) acc.push(moment)
    })

    let newDates = _.chain(acc)
      .map((m) => m.format("YYYY-MM-DD"))
      .uniq()
      .thru((array) => decorateDates(array, date))
      // .chunk(5)
      // .map(group => { return {id: _.map(group, 'dateString').join("_"), dates: group} })
      .value()

    //we need to get weekChunks to make the CP run - otherwise lessons come back as undefined
    get(this, "weekChunks")
    set(this, "dateObjects", newDates)
    set(this, "dateStrings", newDates)
    set(this, "dateToShowAtTop", date)
    set(this, "currentYearMonth", moment(get(this, "date")).format("YYYY-MM"))
    set(this, "dateRangeString", moment(get(this, "date")).format("MMMM YYYY"))

    setProperties(this, {
      dateStrings: newDates,
      dateToShowAtTop: date,
      currentYearMonth: moment(get(this, "date")).format("YYYY-MM"),
      dateRangeString: moment(get(this, "date")).format("MMMM YYYY"),
    })
  }

  /**
    Flow:
      - didReceiveAttrs
        create the dates

      - page forward/back
        push into the dates

      - on click
        set the date

      - on scroll
        update `upperLeftDate`
   */

  // CPs/ getters
  @computed("dateToShowAtTop", "weekChunks.length")
  get rangeToShowAtTopId() {
    let dateToShowAtTop = this.dateToShowAtTop
    return _.chain(this.weekChunks)
      .find((chunk) => includes(chunk.id, dateToShowAtTop))
      .get("id")
      .value()
  }

  @computed("dateObjects.length")
  get weekChunks() {
    return _.chain(this.dateObjects)
      .sortBy("dateString")
      .chunk(5)
      .map((group) => {
        return {
          id: _.map(group, "dateString").sort().join("_"),
          dates: group,
        }
      })
      .map((group) => {
        let cachedGroup = this._groups[group.id]
        if (cachedGroup) {
          return cachedGroup
        } else {
          this._groups[group.id] = group
          return group
        }
      })
      .value()
  }

  // Actions
  @action
  back() {
    focusCourse(null)
    set(this, "focusedCourseId", null)
    set(this, "dateStringForSidebar", null)
    set(this, "sidebarIsVisible", false)
    let firstDate = _.first(this.dateStrings)!
    let newDate = moment(firstDate.dateString).subtract(1, "week").format("YYYY-MM-DD")
    this.setNewDate(newDate)
  }

  @action
  forward() {
    focusCourse(null)
    set(this, "focusedCourseId", null)
    set(this, "dateStringForSidebar", null)
    set(this, "sidebarIsVisible", false)
    let lastDate = _.last(this.dateStrings)!
    let newDate = moment(lastDate.dateString).add(1, "week").format("YYYY-MM-DD")
    this.setNewDate(newDate)
  }

  @action
  onLessonFocus(dateString, courseId) {
    if (dateString === undefined || courseId === undefined)
      throw Error("No date or courseId passed to onLessonFocus")
    focusCourse(courseId)
    set(this, "focusedCourseId", courseId)
    set(this, "dateStringForSidebar", dateString)
    set(this, "sidebarIsVisible", true)
    this.setNewDate(dateString)
  }

  @action
  closeSidebar() {
    set(this, "sidebarIsVisible", false)
  }

  @action
  showSidebar(dateString: string) {
    this.setNewDate(dateString)
    this.set("sidebarIsVisible", true)
  }

  @action setIsPinned([e]) {
    if (e && !isNil(e.intersectionRatio)) {
      this.isPinned = e.intersectionRatio === 0
    }
  }

  gridIndexesToRecalc: Array<string> = []
  rafToken = new Token()

  @action
  recalcHeights(gridIndex: string) {
    this.gridIndexesToRecalc.push(gridIndex)
    // @ts-ignore
    this._recalcHeights.perform()
  }

  _recalcHeights = task({ keepLatest: true }, async () => {
    // Read the dom
    let indexHeights
    scheduler.schedule(
      "measure",
      () => {
        indexHeights = map(this.gridIndexesToRecalc, (gridIndex) => {
          let heights = map($(`[data-grid-index="${gridIndex}"]`), (el) => el.offsetHeight)
          let maxHeight = max(heights)
          return { gridIndex, maxHeight }
        })
        this.gridIndexesToRecalc = []
      },
      this.rafToken
    )

    scheduler.schedule(
      "affect",
      () => {
        // write the dom. Good to not have these interspersed
        forEach(indexHeights, (tuple) => {
          $(`[data-grid-index="${tuple.gridIndex}"]`).css("min-height", `${tuple.maxHeight}px`)
        })
      },
      this.rafToken
    )

    await timeout(30)
  })

  willDestroyElement() {
    scheduler.forget(this.rafToken)
  }

  @action
  printOrDownload() {
    if (this.session.isAuthenticated !== true) {
      this.session.mustSignInToProceed("Print/Download Month")
      return
    }

    let startDate = get(first(this.dateStrings)!, "dateString")
    let endDate = get(last(this.dateStrings)!, "dateString")

    if (includes(this.session.user.content.meta.permissions, "can-print-month")) {
      this.modalManager.openModal("planner/modals/print-download", {
        courseIds: _.chain(this.courses)
          .map((course) => course.id)
          .reject((courseId) => includes(this.hiddenCourseIds, courseId))
          .value(),
        isFilteringClasses: this.isFilteringClasses,
        classesLinedUp: this.classesLinedUp,
        planbookId: this.planbook.id,
        startDate: startDate,
        endDate: endDate,
        currentUserId: this.session.userId,
        dateRangeString: this.dateRangeString,
        layout: "planbook-month:grid",
        canPrint: includes(this.session.user.content.meta.permissions, "can-print-month"),
      })
    } else {
      this.modalManager.openModal("planner/modals/print-paywall", {})
    }
  }
}
